import { useMemo } from 'react';

/**
 * Runway stripe marker
 */
function RunwayStripe({
  position,
  width = 0.5,
  length = 2,
}: {
  position: [number, number, number];
  width?: number;
  length?: number;
}): React.JSX.Element {
  return (
    <mesh position={position} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
      <planeGeometry args={[length, width]} />
      <meshStandardMaterial color="#ffffff" roughness={0.6} />
    </mesh>
  );
}

/**
 * Runway edge light
 */
function RunwayLight({
  position,
  color = '#22c55e',
}: {
  position: [number, number, number];
  color?: string;
}): React.JSX.Element {
  return (
    <group position={position}>
      {/* Light fixture */}
      <mesh castShadow>
        <boxGeometry args={[0.15, 0.3, 0.15]} />
        <meshStandardMaterial color="#374151" roughness={0.5} metalness={0.6} />
      </mesh>
      {/* Light bulb */}
      <mesh position={[0, 0.2, 0]}>
        <sphereGeometry args={[0.08, 8, 8]} />
        <meshStandardMaterial
          color={color}
          emissive={color}
          emissiveIntensity={1}
        />
      </mesh>
      {/* Point light */}
      <pointLight color={color} intensity={0.5} distance={5} decay={2} />
    </group>
  );
}

/**
 * Ground/terrain plane
 */
function Ground(): React.JSX.Element {
  return (
    <mesh position={[0, -0.1, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
      <planeGeometry args={[200, 200]} />
      <meshStandardMaterial color="#4ade80" roughness={0.9} />
    </mesh>
  );
}

/**
 * Airport runway component
 * Features: runway surface, center stripes, edge lights, threshold markings
 */
export function Runway(): React.JSX.Element {

  // Generate center line stripes
  const centerStripes = useMemo(() => {
    const stripes: Array<{ position: [number, number, number]; key: number }> = [];
    for (let x = -40; x < 40; x += 5) {
      stripes.push({
        position: [x, 0.01, 0],
        key: x,
      });
    }
    return stripes;
  }, []);

  // Generate edge lights
  const edgeLights = useMemo(() => {
    const lights: Array<{
      position: [number, number, number];
      color: string;
      key: string;
    }> = [];

    // Left side lights
    for (let x = -35; x < 35; x += 10) {
      lights.push({
        position: [x, 0, 4],
        color: '#22c55e',
        key: `left-${x}`,
      });
    }

    // Right side lights
    for (let x = -35; x < 35; x += 10) {
      lights.push({
        position: [x, 0, -4],
        color: '#22c55e',
        key: `right-${x}`,
      });
    }

    // Threshold lights (green at start)
    for (let z = -3; z <= 3; z += 1.5) {
      lights.push({
        position: [-38, 0, z],
        color: '#22c55e',
        key: `threshold-${z}`,
      });
    }

    // End lights (red at end)
    for (let z = -3; z <= 3; z += 1.5) {
      lights.push({
        position: [38, 0, z],
        color: '#ef4444',
        key: `end-${z}`,
      });
    }

    return lights;
  }, []);

  // Threshold markings
  const thresholdMarkings = useMemo(() => {
    const markings: Array<{ position: [number, number, number]; key: number }> = [];
    for (let z = -2.5; z <= 2.5; z += 1) {
      markings.push({
        position: [-35, 0.01, z],
        key: z,
      });
    }
    return markings;
  }, []);

  return (
    <group>
      {/* Ground/grass */}
      <Ground />

      {/* Main runway surface */}
      <mesh position={[0, 0, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <planeGeometry args={[80, 8]} />
        <meshStandardMaterial color="#374151" roughness={0.7} />
      </mesh>

      {/* Runway edge lines */}
      <mesh position={[0, 0.01, 3.5]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[80, 0.15]} />
        <meshStandardMaterial color="#ffffff" roughness={0.6} />
      </mesh>
      <mesh position={[0, 0.01, -3.5]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[80, 0.15]} />
        <meshStandardMaterial color="#ffffff" roughness={0.6} />
      </mesh>

      {/* Center line dashes */}
      {centerStripes.map((stripe) => (
        <RunwayStripe key={stripe.key} position={stripe.position} />
      ))}

      {/* Threshold markings */}
      {thresholdMarkings.map((marking) => (
        <RunwayStripe
          key={marking.key}
          position={marking.position}
          width={0.4}
          length={5}
        />
      ))}

      {/* Edge lights */}
      {edgeLights.map((light) => (
        <RunwayLight
          key={light.key}
          position={light.position}
          color={light.color}
        />
      ))}

      {/* Runway number "09" */}
      <group position={[-32, 0.02, 0]}>
        {/* Number 0 - simplified as ring */}
        <mesh position={[0, 0, -0.8]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[0.4, 0.6, 16]} />
          <meshStandardMaterial color="#ffffff" roughness={0.6} />
        </mesh>
        {/* Number 9 - simplified */}
        <mesh position={[0, 0, 0.8]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[0.3, 0.5, 16, 1, 0, Math.PI * 1.5]} />
          <meshStandardMaterial color="#ffffff" roughness={0.6} />
        </mesh>
        <mesh position={[0.3, 0, 0.8]} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[0.2, 0.6]} />
          <meshStandardMaterial color="#ffffff" roughness={0.6} />
        </mesh>
      </group>
    </group>
  );
}
